// Pending workspace invitations — the WorkspaceInvitation rows created via
// POST /workspaces/:workspaceId/invitations (WorkspaceController.createInvitation /
// WorkspaceMemberService.invite). This is the SAME model the Simone AI
// assistant's `invite_member` tool writes to (server/src/domains/agents/
// services/workspaceTools.js executeInviteMember → WorkspaceMemberService.invite),
// so an invite Simone sends shows up here too.
//
// Distinct from the org-scoped OrgInvitation (createOrgInvitation /
// InviteDropdown magic-link) and the team-scoped TeamInvitation
// (inviteToTeam / pages/admin/teams.js Reinvite) — those are different
// Mongo collections with their own list/revoke surfaces.
//
// The server gates listWorkspaceInvitations to workspace owner/admin
// (requireWorkspaceRole(['owner','admin'])) — a non-admin caller gets a
// 403, not a filtered/empty list — so callers MUST gate on
// currentUserIsAdmin() before fetching, same as the render gate on the
// /people page section.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
import { activeWorkspaceId } from './scopedClient.js'

const _shellGetSdk = () => {
  if (typeof window === 'undefined') return null
  return shellGetSDK()
}

// SDK list-response unwrap — listWorkspaceInvitations returns
// `{success, data:{items}}` per WorkspaceController.listInvitations; also
// tolerate a bare array / `{items}` shape for resilience.
const _shellUnwrapList = (r) => r?.data?.items || r?.items || r?.data || (Array.isArray(r) ? r : [])

const _label = (raw) => (raw || 'editor').replace(/^./, (c) => c.toUpperCase())

const _mapInvite = (i) => ({
  id: String(i._id || i.id || ''),
  email: i.email || '',
  role: i.role || 'editor',
  roleLabel: _label(i.role),
  invitedAt: i.createdAt || null,
  expiresAt: i.expiresAt || null
})

// In-flight dedup + short-TTL cache, keyed by workspace id — mirrors
// listOrgMembers.js so a page mount + any sidebar refresh don't fan out
// duplicate /workspaces/:id/invitations requests.
let _pendingInvitesInflight = null
let _pendingInvitesInflightWs = null
let _pendingInvitesCache = null
let _pendingInvitesCacheAt = 0
let _pendingInvitesCacheWs = null
// Named distinctly from `getWorkspaceCached.js`'s OWN (different-value:
// 8000 vs this file's 10000) top-level `TTL_MS` — two consts sharing one
// generic name get esbuild-renamed in the merged bundle (`TTL_MS`/
// `TTL_MS2`), and this file's read was owned by no declaration frank's
// checker could attribute. Because the two values genuinely differ, an
// unlucky future rename/reorder could have silently swapped which TTL this
// file's cache used — a locally-unique name removes that risk entirely.
const PENDING_INVITE_TTL_MS = 10000

// el.call('loadPendingInvitations', s.root) — fetches (or serves the cached)
// pending invitation list and writes it to root.pendingInvitations. No-op
// (clears the list) when there's no active workspace or the caller isn't a
// workspace/org admin, so a member never fires the request that always 403s.
// The request itself, a named module function (not an async IIFE held in a
// local: frank lifted that local as a cut-off globalScope entry that did not
// parse). The latches carry unique names: a bare `_cache` collided with
// another module's and shipped renamed (`_cache2`).
const _fetchPendingInvitations = async (sdk, root, wsId) => {
  const raw = await sdk.listWorkspaceInvitations(wsId).catch((err) => {
    console.warn('[pendingInvitations] list failed:', err?.message || err)
    return null
  })
  const list = _shellUnwrapList(raw).map(_mapInvite)
  _pendingInvitesCache = list
  _pendingInvitesCacheAt = Date.now()
  _pendingInvitesCacheWs = wsId
  root?.update?.({ pendingInvitations: list })
  return list
}

export async function loadPendingInvitations(root) {
  const wsId = activeWorkspaceId(root)
  if (!wsId) {
    root?.update?.({ pendingInvitations: [] })
    return []
  }
  if (
    _pendingInvitesCache &&
    _pendingInvitesCacheWs === wsId &&
    Date.now() - _pendingInvitesCacheAt < PENDING_INVITE_TTL_MS
  ) {
    root?.update?.({ pendingInvitations: _pendingInvitesCache })
    return _pendingInvitesCache
  }
  if (_pendingInvitesInflight && _pendingInvitesInflightWs === wsId) return _pendingInvitesInflight
  const sdk = _shellGetSdk()
  if (!sdk || typeof sdk.listWorkspaceInvitations !== 'function') return []
  const inflight = _fetchPendingInvitations(sdk, root, wsId)
  _pendingInvitesInflight = inflight
  _pendingInvitesInflightWs = wsId
  try {
    return await inflight
  } finally {
    if (_pendingInvitesInflight === inflight) {
      _pendingInvitesInflight = null
      _pendingInvitesInflightWs = null
    }
  }
}

// Drop the cache after a mutation (revoke) so the next loadPendingInvitations
// call re-fetches instead of serving the stale list.
export function invalidatePendingInvitations() {
  _pendingInvitesCache = null
  _pendingInvitesCacheAt = 0
  _pendingInvitesCacheWs = null
}

// el.call('revokePendingInvitation', s.root, inviteId) — revokes a pending
// invite and optimistically drops it from root.pendingInvitations on
// success (matches the updateOrgMember / updateTeamCategory "confirm then
// reflect" pattern elsewhere in this package).
export async function revokePendingInvitation(root, inviteId) {
  const wsId = activeWorkspaceId(root)
  if (!wsId || !inviteId) throw new Error('workspaceId and inviteId are required')
  const sdk = _shellGetSdk()
  if (!sdk || typeof sdk.revokeWorkspaceInvitation !== 'function') {
    throw new Error('SDK not available')
  }
  await sdk.revokeWorkspaceInvitation(wsId, inviteId)
  invalidatePendingInvitations()
  const next = (root?.pendingInvitations || []).filter((i) => i.id !== inviteId)
  root?.update?.({ pendingInvitations: next })
  return next
}
